/**
 * Pilot-origin allowlist for the /pilot-auth popup handoff.
 *
 * The /pilot-auth page mints a firefleet-scoped token pair and hands it back to
 * the Firefly Pilot GCS via `window.opener.postMessage(..., <pilotOrigin>)`.
 * `<pilotOrigin>` comes from the `?origin=` query param the opener set, so it is
 * ATTACKER-INFLUENCED. This module is the boundary that decides whether we are
 * willing to post tokens to it. It must be validated BEFORE any postMessage, and
 * postMessage must always target the exact validated origin — NEVER '*'.
 *
 * Where the pilot app is actually served (why these values):
 *  - LAN (the ~95% case): the GCS has the fixed address 192.168.0.51 and the
 *    pilot frontend is published on :3000 — dev (compose.dev.yml `3000:3000`)
 *    and production on the GCS (deploy_install_gcs.sh maps `3000:80`) both use
 *    host port 3000. So the LAN pilot origin is `http://192.168.0.51:3000`.
 *  - VPN (rare, per-server): the GCS is reached at its VPN client IP, pilot app
 *    still on :3000. The VPN client space is 10.8.0.0/16 (operator-confirmed:
 *    "vpn is always 10.8.x.x"). Migrating to Tailscale soon — see MIGRATION below.
 *  - Dev: localhost / 127.0.0.1 / ::1 / *.local, any port.
 *
 * VPN_CIDRS is operator-confirmed: the VPN is always 10.8.x.x (10.8.0.0/16).
 * MIGRATION (soon): the fleet is moving to Tailscale — at cutover, add the
 * Tailscale CGNAT range '100.64.0.0/10' to VPN_CIDRS (and drop 10.8.0.0/16 once
 * fully migrated). Everything here fails CLOSED — an unrecognized origin is refused.
 */

// Pilot frontend host port. Same in dev and prod (see header). If a prod
// variant ever serves the pilot app on another port, add it here.
export const PILOT_PORTS = ['3000'];

// The GCS's fixed LAN address (documented fixed IP; the common case).
export const LAN_GCS_HOST = '192.168.0.51';

// VPN client address space (rare per-server-over-VPN case). Operator-confirmed:
// the VPN is always 10.8.x.x → 10.8.0.0/16. (Tailscale migration: add
// '100.64.0.0/10' here at cutover — see MIGRATION note in the file header.)
export const VPN_CIDRS = ['10.8.0.0/16'];

/** Parse a dotted-quad IPv4 string to an unsigned 32-bit int, or null. */
function ipv4ToInt(ip) {
  const parts = ip.split('.');
  if (parts.length !== 4) return null;
  let n = 0;
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part)) return null;
    const octet = Number(part);
    if (octet > 255) return null;
    n = (n << 8) + octet;
  }
  return n >>> 0;
}

/** True if IPv4 `ip` falls inside `cidr` (e.g. '10.8.0.0/24'). */
function inCidr(ip, cidr) {
  const [range, bitsStr] = cidr.split('/');
  const bits = Number(bitsStr);
  const ipInt = ipv4ToInt(ip);
  const rangeInt = ipv4ToInt(range);
  if (ipInt === null || rangeInt === null) return false;
  if (!(bits >= 0 && bits <= 32)) return false;
  const mask = bits === 0 ? 0 : (~0 << (32 - bits)) >>> 0;
  return (ipInt & mask) === (rangeInt & mask);
}

// Dev-host trust is only granted in non-production builds. In production the
// only accepted origins are the fixed LAN IP + the VPN CIDRs, so a prod build
// never trusts loopback / *.local. `NODE_ENV` is statically inlined by the
// build (same mechanism config.js relies on).
const ALLOW_DEV_HOSTS = process.env.NODE_ENV !== 'production';

/** Dev-only hosts — inherently trusted (attacker can't be the victim's loopback). */
function isDevHost(host) {
  if (!ALLOW_DEV_HOSTS) return false;
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '::1' ||
    host === '[::1]' ||
    host.endsWith('.local')
  );
}

function isPilotPort(port) {
  // URL.port is '' for the scheme default (80/443). Pilot always carries :3000.
  return PILOT_PORTS.includes(port);
}

/**
 * The gate. Returns true only for an origin we are willing to postMessage
 * tokens to. Fails closed on anything unparseable, non-http(s), or unrecognized.
 *
 * @param {string} origin - e.g. "http://192.168.0.51:3000" (from ?origin=)
 * @returns {boolean}
 */
export function isAllowedPilotOrigin(origin) {
  if (!origin || typeof origin !== 'string') return false;

  let url;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }

  // Only http/https, and no embedded credentials.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  if (url.username || url.password) return false;

  // An origin is scheme://host[:port] only — a path/query/hash means it isn't a
  // bare origin string and we don't trust it.
  if ((url.pathname && url.pathname !== '/') || url.search || url.hash) return false;

  const host = url.hostname;
  const port = url.port;

  // Dev hosts — any port.
  if (isDevHost(host)) return true;

  // LAN GCS — fixed IP on the pilot port.
  if (host === LAN_GCS_HOST && isPilotPort(port)) return true;

  // VPN — per-server VPN IP on the pilot port.
  if (isPilotPort(port) && VPN_CIDRS.some((cidr) => inCidr(host, cidr))) return true;

  return false;
}
